import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, Loader2, Bot, User, X, Minimize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import ReactMarkdown from "react-markdown";

interface Message {
  role: "user" | "assistant";
  content: string;
  suggestions?: string[];
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/support-chatbot`;

// Parse suggestions from AI response - handles both newline-separated and inline formats
const parseResponse = (content: string): { text: string; suggestions: string[] } => {
  const suggestionsMatch = content.match(/---SUGGESTIONS---\s*([\s\S]*?)\s*---END---/);
  if (suggestionsMatch) {
    const rawSuggestions = suggestionsMatch[1].trim();
    let suggestions: string[] = [];
    
    if (rawSuggestions.includes("\n")) {
      suggestions = rawSuggestions
        .split("\n")
        .map(s => s.trim())
        .filter(s => s.length > 0);
    } else {
      suggestions = rawSuggestions
        .split("?")
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(s => s + "?");
    }
    
    const text = content.replace(/---SUGGESTIONS---[\s\S]*?---END---/, "").trim();
    return { text, suggestions };
  }
  return { text: content, suggestions: [] };
};

export function SupportChatbot() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize greeting message when language changes
  useEffect(() => {
    setMessages([
      {
        role: "assistant",
        content: t.chatbot.greeting,
      },
    ]);
  }, [t.chatbot.greeting]);

  const quickQuestions = [
    t.chatbot.q1,
    t.chatbot.q2,
    t.chatbot.q3,
    t.chatbot.q4,
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && !minimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, minimized]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    let assistantContent = "";

    const updateAssistant = (chunk: string, isFinal = false) => {
      assistantContent += chunk;
      
      const { text, suggestions } = isFinal 
        ? parseResponse(assistantContent) 
        : { text: assistantContent, suggestions: [] };
      
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && prev.length > 1 && prev[prev.length - 2].role === "user") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: text, suggestions } : m));
        }
        return [...prev, { role: "assistant", content: text, suggestions }];
      });
    };

    try {
      const allMessages = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: allMessages }),
      });

      if (!resp.ok || !resp.body) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to get response");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) updateAssistant(content);
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      // Handle remaining buffer
      if (buffer.trim()) {
        for (let raw of buffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) updateAssistant(content);
          } catch {
            /* ignore */
          }
        }
      }
      
      // Final update to parse suggestions
      updateAssistant("", true);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: t.chatbot.errorMessage,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickQuestion = (question: string) => {
    sendMessage(question);
  };

  const toggleChat = () => {
    if (!open) {
      setOpen(true);
      setMinimized(false);
    } else if (minimized) {
      setMinimized(false);
    } else {
      setMinimized(true);
    }
  };

  const closeChat = () => {
    setOpen(false);
    setMinimized(false);
  };

  return (
    <>
      {/* Floating Chat Panel */}
      {open && (
        <div 
          className={cn(
            "fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-40 bg-background border rounded-xl shadow-2xl transition-all duration-200 overflow-hidden",
            minimized ? "w-72 h-14" : "w-96 max-w-[calc(100vw-3rem)] h-[500px] max-h-[70vh]"
          )}
        >
          {/* Header */}
          <div 
            className="px-4 py-3 border-b bg-muted/50 flex items-center justify-between cursor-pointer"
            onClick={() => minimized && setMinimized(false)}
          >
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">{t.chatbot.assistant}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  setMinimized(!minimized);
                }}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => {
                  e.stopPropagation();
                  closeChat();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Chat Content - hidden when minimized */}
          {!minimized && (
            <>
              <ScrollArea className="flex-1 h-[calc(100%-110px)] px-4 py-3" ref={scrollRef}>
                <div className="space-y-4">
                  {messages.map((message, index) => {
                    const isLastAssistant = message.role === "assistant" && index === messages.length - 1;
                    const showSuggestions = isLastAssistant && message.suggestions && message.suggestions.length > 0 && !isLoading;
                    
                    return (
                      <div key={index}>
                        <div
                          className={cn(
                            "flex gap-2",
                            message.role === "user" ? "justify-end" : "justify-start"
                          )}
                        >
                          {message.role === "assistant" && (
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Bot className="h-3.5 w-3.5 text-primary" />
                            </div>
                          )}
                          <div
                            className={cn(
                              "rounded-lg px-3 py-2 max-w-[85%] text-sm",
                              message.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            )}
                          >
                            {message.role === "assistant" ? (
                              <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
                                <ReactMarkdown>{message.content}</ReactMarkdown>
                              </div>
                            ) : (
                              message.content
                            )}
                          </div>
                          {message.role === "user" && (
                            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                              <User className="h-3.5 w-3.5 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                        
                        {/* Contextual follow-up suggestions */}
                        {showSuggestions && (
                          <div className="ml-9 mt-3 space-y-2">
                            <p className="text-xs text-muted-foreground">{t.chatbot.relatedQuestions}</p>
                            <div className="flex flex-col gap-1.5">
                              {message.suggestions!.map((q, i) => (
                                <Button
                                  key={i}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-auto py-1.5 px-2.5 justify-start text-left whitespace-normal"
                                  onClick={() => handleQuickQuestion(q)}
                                  disabled={isLoading}
                                >
                                  {q}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {isLoading && messages[messages.length - 1]?.role === "user" && (
                    <div className="flex gap-2 justify-start">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div className="bg-muted rounded-lg px-3 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick questions for first message only */}
                {messages.length === 1 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-muted-foreground">{t.chatbot.quickQuestions}</p>
                    <div className="flex flex-col gap-1.5">
                      {quickQuestions.map((q) => (
                        <Button
                          key={q}
                          variant="outline"
                          size="sm"
                          className="text-xs h-auto py-1.5 px-2.5 justify-start text-left"
                          onClick={() => handleQuickQuestion(q)}
                          disabled={isLoading}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </ScrollArea>

              <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t.chatbot.askPlaceholder}
                  disabled={isLoading}
                  className="flex-1 h-9 text-sm"
                />
                <Button type="submit" size="icon" className="h-9 w-9" disabled={isLoading || !input.trim()}>
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Floating Action Button */}
      <Button
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg z-50"
        size="icon"
        onClick={toggleChat}
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
    </>
  );
}